<template>
    <div v-if="form" class="form-section form-section-gas-card factor-value-form-body">
        <div class="gas-card-header">
            <div class="gas-card-header-left">
                <span class="section-pill section-pill-gray" aria-hidden="true"></span>
                <div class="gas-card-title-wrap">
                    <h3 class="gas-card-title">{{ mode === "create" ? "계수 데이터 입력" : "계수 데이터 수정" }}</h3>
                </div>
                <div class="gas-unit-picker">
                    <span class="gas-unit-picker-label">계수 단위</span>
                    <select v-model="form.gasEmissionUnit" class="form-control gas-unit-picker-select">
                        <option v-for="opt in gasEmissionUnitOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
        <div class="gas-card-body">
            <div class="input-cluster input-cluster-meta">
                <div class="cluster-head">
                    <h4>기본 계수 설정</h4>
                </div>
                <div class="row gas-meta-row">
                    <div class="col-md-4">
                        <div class="form-group start-month-picker-wrapper meta-field">
                            <label>시작월 <span class="required">*</span></label>
                            <DatePicker
                                v-model="form.startMonth"
                                type="month"
                                format="YYYYMM"
                                value-type="format"
                                placeholder="예: 202401"
                                :clearable="false"
                                :disabled="mode === 'edit'"
                                @change="onStartMonthChange"
                            />
                            <small v-if="mode === 'edit'" class="text-muted d-block mt-1"
                                >수정 시 시작월 변경 불가</small
                            >
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group end-month-picker-wrapper meta-field">
                            <label>
                                종료월
                                <span class="text-muted end-month-hint">(선택)</span>
                            </label>
                            <DatePicker
                                v-model="form.endMonth"
                                type="month"
                                format="YYYYMM"
                                value-type="format"
                                placeholder="미입력 시 현재까지"
                                :clearable="true"
                            />
                            <small class="text-muted d-block mt-1">비어 있으면 현재까지 유효</small>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group meta-field">
                            <label>출처/방법론</label>
                            <select v-model="form.source" class="form-control">
                                <option value="">선택</option>
                                <option v-for="opt in factorSourceOptions" :key="opt.value" :value="opt.value">
                                    {{ opt.text }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div class="input-cluster input-cluster-core">
                <div class="cluster-head">
                    <h4>주요 가스 계수</h4>
                    <span class="cluster-head-caption">CO2는 필수 입력</span>
                </div>
                <div class="row gas-factors-row gas-factors-row-2x2">
                    <div class="col-md-6 col-6">
                        <div class="form-group gas-factor-item gas-factor-item--co2">
                            <div class="gas-factor-item-header">
                                <label>CO2 <span class="required">*</span></label>
                                <small class="gas-species-name">{{ getGasSpeciesName("CO2") }}</small>
                                <span class="gas-factor-required">필수</span>
                            </div>
                            <div class="input-group factor-input-group">
                                <input
                                    v-model.number="form.CO2Factor"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    required
                                    placeholder="예: 2.2"
                                />
                                <div class="input-group-append">
                                    <span class="input-group-text factor-suffix">{{ form.gasEmissionUnit }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 col-6">
                        <div class="form-group gas-factor-item gas-factor-item--ch4">
                            <div class="gas-factor-item-header">
                                <label>CH4</label>
                                <small class="gas-species-name">{{ getGasSpeciesName("CH4") }}</small>
                            </div>
                            <div class="input-group factor-input-group">
                                <input
                                    v-model.number="form.CH4Factor"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="0"
                                />
                                <div class="input-group-append">
                                    <span class="input-group-text factor-suffix">{{ form.gasEmissionUnit }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 col-6">
                        <div class="form-group gas-factor-item gas-factor-item--n2o">
                            <div class="gas-factor-item-header">
                                <label>N2O</label>
                                <small class="gas-species-name">{{ getGasSpeciesName("N2O") }}</small>
                            </div>
                            <div class="input-group factor-input-group">
                                <input
                                    v-model.number="form.N2OFactor"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="0"
                                />
                                <div class="input-group-append">
                                    <span class="input-group-text factor-suffix">{{ form.gasEmissionUnit }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 col-6">
                        <div class="form-group gas-factor-item gas-factor-item--sf6">
                            <div class="gas-factor-item-header">
                                <label>SF6</label>
                                <small class="gas-species-name">{{ getGasSpeciesName("SF6") }}</small>
                            </div>
                            <div class="input-group factor-input-group">
                                <input
                                    v-model.number="form.SF6Factor"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="0"
                                />
                                <div class="input-group-append">
                                    <span class="input-group-text factor-suffix">{{ form.gasEmissionUnit }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="input-cluster input-cluster-optional">
                <div class="cluster-head">
                    <h4>확장 가스 계수</h4>
                    <span class="optional-chip">선택 입력</span>
                </div>
                <FactorGasSpeciesBlock
                    basket="HFCs"
                    :rows="form.hfcSpeciesRows"
                    :gas-unit="form.gasEmissionUnit"
                    @update:rows="form.hfcSpeciesRows = $event"
                />
                <FactorGasSpeciesBlock
                    basket="PFCs"
                    :rows="form.pfcSpeciesRows"
                    :gas-unit="form.gasEmissionUnit"
                    @update:rows="form.pfcSpeciesRows = $event"
                />
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonCodes from "../../../codes";
import FactorGasSpeciesBlock from "./FactorGasSpeciesBlock.vue";

export default {
    name: "FactorValueFormBody",
    components: { DatePicker, FactorGasSpeciesBlock },
    inject: ["carbonFactorFormHost"],
    props: {
        mode: {
            type: String,
            required: true,
            validator: (v) => v === "create" || v === "edit",
        },
        gasEmissionUnitOptions: {
            type: Array,
            default: () => [],
        },
        factorSourceOptions: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        form() {
            return this.carbonFactorFormHost?.form || null;
        },
    },
    methods: {
        getGasSpeciesName(code) {
            return CarbonCodes.gasCode?.getLabel?.(code) ?? code ?? "";
        },
        onStartMonthChange() {
            if (typeof this.carbonFactorFormHost?.onStartMonthChange === "function") {
                this.carbonFactorFormHost.onStartMonthChange();
            }
        },
    },
};
</script>

<style scoped>
.factor-value-form-body {
    margin-top: 0;
    border: 0;
    border-radius: 14px;
    background: linear-gradient(180deg, #fbfdff 0%, #f5f8fc 100%);
    padding: 10px 12px 12px;
}

.gas-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    padding-bottom: 0;
    border-bottom: 0;
}

.gas-card-header-left {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
    gap: 8px;
    min-width: 0;
}

.section-pill {
    width: 3px;
    height: 13px;
    margin-top: 1px;
    flex-shrink: 0;
    border-radius: 999px;
    background: #0f766e;
}

.section-pill-gray {
    background: #8fa3b8;
}

.gas-card-title-wrap {
    min-width: 0;
}

.gas-card-title {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #2b4055;
}

.gas-card-subtitle {
    margin: 2px 0 0;
    font-size: 10px;
    line-height: 1.35;
    color: #6d8398;
}

.gas-unit-picker {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 6px;
    padding: 4px 7px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.84);
}

.gas-unit-picker-label {
    font-size: 10px;
    font-weight: 600;
    color: #50667b;
    white-space: nowrap;
}

.gas-unit-picker-select {
    min-width: 120px;
    max-width: 156px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid #d9e3ec;
    box-shadow: none;
    font-size: 11px;
}

.gas-card-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 820px;
    margin: 0 auto;
}

.input-cluster {
    padding: 8px;
    border-radius: 12px;
}

.input-cluster-meta {
    background: rgba(255, 255, 255, 0.72);
}

.input-cluster-core {
    background: rgba(247, 251, 255, 0.85);
}

.input-cluster-optional {
    background: rgba(246, 249, 253, 0.78);
}

.cluster-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 7px;
}

.cluster-head h4 {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    color: #2a3f53;
    letter-spacing: 0.02em;
}

.cluster-head-caption {
    margin-left: auto;
    font-size: 10px;
    font-weight: 600;
    color: #5b7388;
}

.optional-chip {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 999px;
    background: #edf3f9;
    color: #60778d;
    font-size: 10px;
    font-weight: 700;
}

.gas-meta-row {
    margin-left: -4px;
    margin-right: -4px;
}

.gas-meta-row > [class*="col-"] {
    padding-left: 4px;
    padding-right: 4px;
}

.meta-field {
    margin-bottom: 6px;
    padding: 0;
    border: 0;
    background: transparent;
}

.meta-field label {
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 600;
    color: #4f6579;
}

.meta-field .form-control {
}

.meta-field small {
    font-size: 10px;
    margin-top: 3px;
    color: #728498 !important;
}

.end-month-hint {
    font-weight: 400;
    font-size: 10px;
}

.gas-factors-row-2x2 {
    margin-top: 0;
    margin-left: -4px;
    margin-right: -4px;
}

.gas-factors-row-2x2 > [class*="col-"] {
    padding-left: 4px;
    padding-right: 4px;
}

.gas-factor-item {
    margin-bottom: 6px;
    border: 0;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.8);
    padding: 7px;
}

.gas-factor-item--co2 {
    background: linear-gradient(180deg, rgba(244, 249, 254, 0.95) 0%, rgba(238, 245, 252, 0.95) 100%);
}

.gas-factor-item--ch4 {
    background: linear-gradient(180deg, rgba(252, 253, 255, 0.95) 0%, rgba(246, 248, 252, 0.95) 100%);
}

.gas-factor-item--n2o {
    background: linear-gradient(180deg, rgba(252, 253, 255, 0.95) 0%, rgba(247, 250, 254, 0.95) 100%);
}

.gas-factor-item--sf6 {
    background: linear-gradient(180deg, rgba(251, 252, 255, 0.95) 0%, rgba(247, 249, 253, 0.95) 100%);
}

.gas-factor-item-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
}

.gas-factor-item-header label {
    margin: 0;
    font-size: 10px;
    font-weight: 700;
    color: #32485d;
}

.gas-species-name {
    font-size: 10px;
    color: #7b8ea1;
    margin: 0;
}

.gas-factor-required {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 999px;
    border: 0;
    background: #edf4fa;
    font-size: 9px;
    font-weight: 700;
    color: #577289;
}

.factor-input-group {
    flex-wrap: nowrap;
    border: 1px solid #d9e3ed;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    overflow: hidden;
}

.factor-input-group > .form-control {
    border: 0;
    box-shadow: none;
    font-size: 11px;
    height: 30px;
}

.factor-input-group > .form-control:focus {
    box-shadow: none;
}

.factor-suffix {
    border: 0;
    border-left: 1px solid #e2e9f0;
    border-radius: 0;
    background: #f7fafc;
    color: #5f7388;
    font-size: 9px;
    font-weight: 600;
}

:deep(.mx-datepicker) {
    width: 100%;
}

:deep(.mx-input) {
    height: 30px;
    border: 1px solid #d9e3ed;
    border-radius: 8px;
    box-shadow: none;
    font-size: 11px;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.92);
}

:deep(.mx-input:hover) {
    border-color: #becddb;
}

:deep(.mx-input:focus) {
    border-color: #4d84ac;
    box-shadow: 0 0 0 2px rgba(77, 132, 172, 0.14);
}

:deep(.mx-icon-calendar),
:deep(.mx-icon-clear) {
    color: #8398ae;
}

.required {
    color: #c0372c;
}

@media (max-width: 991px) {
    .gas-unit-picker {
        width: 100%;
        justify-content: space-between;
        border-radius: 10px;
    }

    .gas-unit-picker-select {
        flex: 1 1 auto;
        max-width: 100%;
    }

    :deep(.mx-input),
    .factor-input-group > .form-control,
    .meta-field .form-control {
        max-width: 100%;
    }
}

@media (max-width: 767px) {
    .factor-value-form-body {
        padding: 8px;
    }

    .gas-card-header {
        margin-bottom: 6px;
    }

    .meta-field,
    .gas-factor-item {
        padding: 6px;
    }
}
</style>
